import { create } from "zustand";
import { persist } from "zustand/middleware";
import apiClient from "../utils/apiClient";

const useTestStore = create(
  persist(
    (set, get) => ({
      // State
      test: null,
      sections: [],
      loading: false,
      error: null,
      question: {},
      currentSectionIndex: 0,
      currentQuestionIndex: 0,
      // responses: {},
      instance: {},
      responses: [],
      result: {},
      currentDomain: null,

      fetchInstance: async (domain_id) => {
        try {
          const response = await apiClient.get(
            `/tests/find-test-instance/${domain_id}`
          );

          if (response.data.success) {
            const instance = response.data.test_instance;
            // Fetch result after fetching instance
            if (instance && instance.instance_id) {
              await useTestStore.getState().updateResult(instance.instance_id);
            }
          } else {
            console.error(
              "Failed to fetch test instance:",
              response.data.message
            );
          }
        } catch (error) {
          console.error("Error fetching test instance:", error);
        } finally {
          set({ loading: false });
        }
      },

      setCurrentDomain: (domain) => set({ currentDomain: domain }),

      // Create test instance
      createTestInstance: async (testId, domainId, language) => {
        set({ loading: true });
        try {
          const activeTest = await apiClient.get(
            `/tests/active-instance/${domainId}/${testId}`
          );
          //active instance found
          if (activeTest.data.data.length > 0) {
            set({
              instance: activeTest.data.data[0],
              loading: false,
            });
          } else {
            const data = await apiClient.post("/tests/create-test-instance", {
              test_id: testId,
              domain_id: domainId,
              language: language,
            });
            if (!data.data.success) {
              throw new Error(data.message || "Failed to create test instance");
            }
            set({ instance: data.data.test_instance, loading: false });
          }
          // console.log("Test instance : ", data);
          // console.log("Data.data.test_instance : ", data.data.test_instance);
        } catch (error) {
          set({ error: error.message, loading: false });
        }
      },

      // Fetch test and sections from backend
      fetchTest: async (testId) => {
        set({ loading: true });

        try {
          const res = await fetch(
            ` https://vhvkknrqif.ap-south-1.awsapprunner.com/api/tests/test-data/${testId}`
          );
          const data = await res.json();

          set({
            test: data.test,
            sections: data.sections,
            loading: false,
          });
        } catch (error) {
          set({ error: "Failed to load test data", loading: false });
        }
      },

      // Fetch question
      fetchQuestion: async (questionId, domainId, language) => {
        set({ loading: true });
        try {
          const res = await apiClient.get(
            `tests/questions/${questionId}/${domainId}/${language}`
          );
          set({ question: res.data.data, loading: false });
        } catch (error) {
          set({ error: "Failed to load question data", loading: false });
        }
      },

      // Handle user response
      setResponse: async (
        instanceId,
        questionId,
        userResponse,
        timeTakenSeconds
      ) => {
        set({ loading: true });
        try {
          const res = await fetch(
            "https://vhvkknrqif.ap-south-1.awsapprunner.com/api/tests/store-response",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                instance_id: instanceId,
                question_id: questionId,
                user_response: userResponse,
                time_taken_seconds: timeTakenSeconds,
              }),
            }
          );
          const data = await res.json();

          if (!res.ok) {
            throw new Error(data.message || "Failed to store response");
          }

          set((state) => {
            //to avoid adding duplicate responses
            const updatedResponses = state.responses.filter(
              (r) => r.questionId !== questionId
            );
            return {
              responses: [
                ...updatedResponses,
                { questionId, userResponse, isCorrect: data.isCorrect },
              ],
              loading: false,
            };
          });
        } catch (error) {
          set({ error: error.message, loading: false });
        }
      },
      updateResult: async (instanceId) => {
        set({ loading: true });

        try {
          const res = await apiClient.get(`/tests/submit/${instanceId}`);
          console.log("res", res);
          const data = res.data;

          // Store all test result data in Zustand
          set({
            result: {
              total_score: data.total_score,
              passed: data.passed,
              skill_score: data.skill_score,
              knowledge_score: data.knowledge_score,
              behaviour_score: data.behaviour_score,
              iq_score: data.iq_score,
              emotional_intelligence_score: data.emotional_intelligence_score,
              decision_making_score: data.decision_making_score,
              interest_score: data.interest_score,
            },
            loading: false,
          });
        } catch (error) {
          set({ error: error.message, loading: false });
        }
      },

      // Handle navigation
      nextQuestion: () =>
        set((state) => ({
          currentQuestionIndex: state.currentQuestionIndex + 1,
        })),
      prevQuestion: () =>
        set((state) => ({
          currentQuestionIndex: state.currentQuestionIndex - 1,
        })),
      nextSection: () =>
        set((state) => ({
          currentSectionIndex: state.currentSectionIndex + 1,
          currentQuestionIndex: 0,
        })),
      prevSection: () =>
        set((state) => ({
          currentSectionIndex: state.currentSectionIndex - 1,
          currentQuestionIndex: 0,
        })),

      startTest: () => set({ actual_start_time: new Date().toISOString() }),

      completeTest: () => set({ completion_time: new Date().toISOString() }),

      resetTest: () => {
        const partialReset = {
          test: null,
          sections: [],
          responses: [],
          currentSectionIndex: 0,
          currentQuestionIndex: 0,
          actual_start_time: null,
          completion_time: null,
        };

        set(partialReset);

        // Force update in localStorage
        const currentState = useTestStore.getState();
        const updatedState = { ...currentState, ...partialReset };
        localStorage.setItem(
          "test-storage",
          JSON.stringify({ state: updatedState })
        );
      },
    }),
    {
      name: "test-storage", // Key for localStorage
    }
  )
);

export default useTestStore;
